import { motion } from 'framer-motion';
import { PlayCircle, Clock, TrendingUp } from 'lucide-react';

// TODO: Connect to Supabase drills_curriculum table
interface Drill {
  name: string;
  stage: string;
  progress: number;
  duration: string;
  type: string;
}

interface CurriculumTileProps {
  drills?: Drill[];
  onDrillClick?: (drill: Drill) => void;
  onViewAll?: () => void;
}

/**
 * Curriculum Tile - Active drills with progress tracking
 * From FIGMA Clubhouse 2.0 design
 * 
 * @TODO Wire to useQuery for drills_curriculum table
 */
export function CurriculumTile({
  drills,
  onDrillClick,
  onViewAll,
}: CurriculumTileProps) {
  // Mock data - replace with real drills when connected to Supabase
  const defaultDrills: Drill[] = [
    {
      name: 'Impact Position Fundamentals',
      stage: 'Stage 2',
      progress: 65,
      duration: '12 min',
      type: 'Active',
    },
    {
      name: 'Weight Transfer Sequence',
      stage: 'Stage 1',
      progress: 40,
      duration: '8 min',
      type: 'In Progress',
    },
  ];

  const activeDrills = drills || defaultDrills;

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15)' }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-2xl shadow-md overflow-hidden h-full"
      data-figma-id="curriculum-tile"
    >
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 px-6 py-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white">Your Curriculum</h3>
          <TrendingUp className="w-5 h-5 text-emerald-200" />
        </div>
        <p className="text-emerald-100 text-sm">Active drills & progression</p>
      </div>

      <div className="p-6 space-y-4">
        {activeDrills.map((drill, index) => (
          <motion.div
            key={`${drill.name}-${index}`}
            whileHover={{ scale: 1.02 }}
            onClick={() => onDrillClick?.(drill)}
            className="border border-slate-200 rounded-xl p-4 cursor-pointer transition-all hover:border-emerald-300 hover:shadow-sm"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h4 className="text-slate-900 mb-1">{drill.name}</h4>
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md">
                    {drill.stage}
                  </span>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{drill.duration}</span>
                  </div>
                </div>
              </div>
              <PlayCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 ml-2" />
            </div>

            {/* Progress Bar */}
            <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${drill.progress}%` }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full"
              />
            </div>
            <div className="mt-1.5 text-xs text-slate-500">
              {drill.progress}% complete
            </div>
          </motion.div>
        ))}

        <button
          onClick={onViewAll}
          className="w-full mt-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <span>View All Drills</span>
        </button>
      </div>
    </motion.div>
  );
}

